import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';
import { SalesOrderDTO } from '@app/api/purchase-service-sales';
import { WarehouseOptionsService } from '@shared/services/warehouse-options.service';
import { parsePositiveIntRouteParam } from '@shared/utils/route-param.util';
import { generateSeries } from '../../distribution.constants';
import { SalesRequestService } from '../../sales-request/services/sales-request.service';
import { SalesOrderService } from '../services/sales-order.service';

interface OrderLine {
  salesRequestItemId?: number;
  skuCode: string;
  quantity: number | null;
  /** Optional: price-service quotes the DISTRIBUTION dealer price first. */
  unitPrice: number | null;
  warehouseCode: string | null;
}

interface WarehouseOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-sales-order-add',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    DatePickerModule,
    InputNumberModule,
    InputTextModule,
    SelectModule,
    ToastModule,
  ],
  providers: [MessageService],
  templateUrl: './sales-order-add.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SalesOrderAddComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly orderService = inject(SalesOrderService);
  private readonly requestService = inject(SalesRequestService);
  private readonly warehouseOptionsService = inject(WarehouseOptionsService);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);

  readonly saving = signal(false);
  readonly lines = signal<OrderLine[]>([this.emptyLine()]);
  readonly warehouses = signal<WarehouseOption[]>([]);
  readonly minDate = new Date();

  series = generateSeries('SO');
  vendorCode = '';
  deliveryDate: Date | null = null;
  description = '';
  readonly salesRequestId = signal<number | null>(null);

  ngOnInit(): void {
    this.loadWarehouses();
    const requestId = parsePositiveIntRouteParam(
      this.route.snapshot.queryParamMap.get('salesRequestId'),
    );
    if (requestId != null) {
      this.prefillFromRequest(requestId);
    }
  }

  private emptyLine(): OrderLine {
    return {
      skuCode: '',
      quantity: 1,
      unitPrice: null,
      warehouseCode: null,
    };
  }

  addLine(): void {
    this.lines.update((lines) => [...lines, this.emptyLine()]);
  }

  removeLine(index: number): void {
    this.lines.update((lines) =>
      lines.length > 1 ? lines.filter((_, i) => i !== index) : lines,
    );
  }

  onBack(): void {
    this.router.navigate(['/distribution/sales-order']);
  }

  onSave(): void {
    const lines = this.lines();
    const valid =
      this.series.trim() &&
      this.vendorCode.trim() &&
      lines.length > 0 &&
      lines.every(
        (l) => l.skuCode.trim() && (l.quantity ?? 0) > 0 && !!l.warehouseCode,
      );
    if (!valid) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.messages.warning'),
        detail: this.translate.instant('distribution.messages.invalid_form'),
      });
      return;
    }

    const body: SalesOrderDTO = {
      salesRequestId: this.salesRequestId() ?? undefined,
      series: this.series.trim(),
      vendorCode: this.vendorCode.trim(),
      deliveryDate: this.endOfDay(this.deliveryDate)?.toISOString(),
      description: this.description.trim() || undefined,
      items: lines.map((l) => ({
        salesRequestItemId: l.salesRequestItemId,
        skuCode: l.skuCode.trim(),
        quantity: l.quantity as number,
        unitPrice: l.unitPrice ?? undefined,
        warehouseCode: l.warehouseCode as string,
      })),
    };

    this.saving.set(true);
    this.orderService.create(body).subscribe({
      next: (created) => {
        this.saving.set(false);
        this.router.navigate(['/distribution/sales-order/view', created.id]);
      },
      error: (err) => {
        this.saving.set(false);
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.messages.error'),
          detail:
            err?.error?.message ??
            this.translate.instant('distribution.messages.failed'),
        });
      },
    });
  }

  /** Delivery date must be future-or-present: use the end of the chosen day. */
  private endOfDay(date: Date | null): Date | null {
    if (!date) {
      return null;
    }
    const end = new Date(date);
    end.setHours(23, 59, 59, 0);
    return end;
  }

  private prefillFromRequest(requestId: number): void {
    this.requestService.getById(requestId).subscribe({
      next: (request) => {
        this.salesRequestId.set(request.id ?? null);
        this.lines.set(
          (request.items ?? []).length > 0
            ? (request.items ?? []).map((item) => ({
                salesRequestItemId: item.id,
                skuCode: item.skuCode ?? '',
                quantity: item.quantity ?? 1,
                unitPrice: null,
                warehouseCode: null,
              }))
            : [this.emptyLine()],
        );
      },
      error: () =>
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.messages.error'),
          detail: this.translate.instant('distribution.messages.failed'),
        }),
    });
  }

  private loadWarehouses(): void {
    this.warehouseOptionsService
      .getOptionsPage({ page: 1, size: 100, searchText: '' })
      .subscribe((result) =>
        this.warehouses.set(
          result.options.map((o) => ({
            label: o.label,
            value: String(o.value),
          })),
        ),
      );
  }
}
